import { useRef, useEffect, useState } from "react";
import { X, Sparkles, Share2, Star, Volume2, Copy, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { MODE_MAP } from "@/lib/modes";
import AskJixxeo from "@/components/AskJixxeo";
import ShareCardStudio from "@/components/ShareCardStudio";
import CollectionPicker from "@/components/CollectionPicker";

export default function ScanResultSheet({ result, onClose, onToggleFavorite }) {
  const ref = useRef(null);
  const [fav, setFav] = useState(result?.favorite || false);
  const [showStudio, setShowStudio] = useState(false);

  useEffect(() => { setFav(result?.favorite || false); }, [result]);
  if (!result) return null;
  const mode = MODE_MAP[result.mode] || MODE_MAP.jixxeo_lens;

  const speak = () => {
    try {
      const u = new SpeechSynthesisUtterance(`${result.title}. ${result.summary}`);
      speechSynthesis.speak(u);
    } catch { /* noop */ }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${result.title}\n\n${result.summary}\n\nScanned with Jixxeo Scan • jixxeoscan.com`);
      toast.success("Copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };
  const share = async () => {
    const text = `I scanned this with Jixxeo Scan — ${result.title}. ${result.summary}\n\nPoint. Tap. Discover. jixxeoscan.com`;
    if (navigator.share) {
      try { await navigator.share({ title: result.title, text }); } catch { /* ignored */ }
    } else {
      await navigator.clipboard.writeText(text);
      toast.success("Share text copied");
    }
  };
  const toggleFav = async () => {
    setFav(!fav);
    onToggleFavorite?.(result, !fav);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="scan-result-sheet">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div ref={ref} className="relative w-full max-w-md jx-glass rounded-t-3xl p-5 pb-8 jx-fade-up max-h-[85vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${mode.color}22`, color: mode.color }}>
              <Sparkles size={18} />
            </div>
            <div className="font-mono-data text-[11px] uppercase tracking-widest text-slate-400">
              Jixxeo AI • {mode.label}
            </div>
          </div>
          <button data-testid="scan-result-close" onClick={onClose} className="text-slate-400 p-1"><X size={20} /></button>
        </div>

        <div className="flex items-start gap-3">
          {result.thumbnail && (
            <img src={result.thumbnail} alt="" className="w-20 h-20 rounded-2xl object-cover border border-white/10" />
          )}
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-2xl font-bold leading-tight" data-testid="scan-result-title">{result.title}</h2>
            <p className="text-sm text-slate-400">{result.subtitle}</p>
            <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-[11px] font-mono-data text-cyan-300">
              <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full" />
              {result.confidence.toFixed(1)}% Match
            </div>
          </div>
        </div>

        <p className="mt-4 text-sm leading-relaxed text-slate-200" data-testid="scan-result-summary">{result.summary}</p>

        {result.details && Object.keys(result.details).length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {Object.entries(result.details).slice(0, 6).map(([k, v]) => (
              <div key={k} className="rounded-xl bg-white/5 border border-white/10 p-2.5">
                <p className="text-[10px] font-mono-data uppercase text-slate-500 tracking-wider">{k}</p>
                <p className="text-sm text-white font-medium truncate">{String(v)}</p>
              </div>
            ))}
          </div>
        )}

        {result.actions?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {result.actions.map((a, i) => (
              <span key={i} className="px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300">{a}</span>
            ))}
          </div>
        )}

        <div className="mt-5 grid grid-cols-5 gap-2">
          <button data-testid="scan-action-speak" onClick={speak} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-white/5 border border-white/10 active:scale-95">
            <Volume2 size={18} /><span className="text-[10px] font-mono-data uppercase">Listen</span>
          </button>
          <button data-testid="scan-action-copy" onClick={copy} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-white/5 border border-white/10 active:scale-95">
            <Copy size={18} /><span className="text-[10px] font-mono-data uppercase">Copy</span>
          </button>
          <button data-testid="scan-action-favorite" onClick={toggleFav} className={`flex flex-col items-center gap-1 p-2 rounded-xl border active:scale-95 ${fav ? "bg-amber-400/10 border-amber-400/50 text-amber-300" : "bg-white/5 border-white/10"}`}>
            <Star size={18} fill={fav ? "currentColor" : "none"} /><span className="text-[10px] font-mono-data uppercase">Save</span>
          </button>
          <button data-testid="scan-action-share-card" onClick={() => setShowStudio(true)} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-white/5 border border-white/10 active:scale-95">
            <ImageIcon size={18} /><span className="text-[10px] font-mono-data uppercase">Card</span>
          </button>
          <button data-testid="scan-action-share" onClick={share} className="flex flex-col items-center gap-1 p-2 rounded-xl bg-cyan-400/10 border border-cyan-400/40 text-cyan-200 active:scale-95">
            <Share2 size={18} /><span className="text-[10px] font-mono-data uppercase">Share</span>
          </button>
        </div>

        <AskJixxeo scan={result} />
        <CollectionPicker scan={result} />

        <p className="mt-4 text-center text-[10px] text-slate-500 font-mono-data uppercase tracking-widest">
          Scanned with Jixxeo AI • jixxeoscan.com
        </p>
      </div>
      {showStudio && <ShareCardStudio scan={result} onClose={() => setShowStudio(false)} />}
    </div>
  );
}
